import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Save, 
  User, 
  Building, 
  DollarSign, 
  Calendar, 
  Star, 
  Target, 
  FileText, 
  Users, 
  Mail, 
  Phone,
  RefreshCw
} from 'lucide-react';

const LeadToOpportunityModal = ({ lead, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data with lead information
  const [formData, setFormData] = useState({
    // Basic Information (pre-filled from lead)
    OpportunityName: lead.Title || lead.ContactName || lead.CompanyName || '',
    Description: lead.Description || '',
    Priority: lead.Priority || 'Medium',
    Category: 'New Business',
    
    // Customer Information (pre-filled from lead)
    CustomerName: lead.CompanyName || lead.ContactName || '',
    ContactName: lead.ContactName || '',
    ContactEmail: lead.ContactEmail || '',
    ContactPhone: lead.ContactPhone || '',
    
    // Financial Information (required for opportunity)
    Value: lead.EstimatedBudget || '',
    Currency: lead.Currency || 'USD',
    Probability: lead.Score || 50,
    
    // Timeline
    ExpectedCloseDate: lead.ExpectedCloseDate ? lead.ExpectedCloseDate.split('T')[0] : '',
    NextFollowUpDate: lead.NextFollowUpDate ? lead.NextFollowUpDate.split('T')[0] : '',
    
    // Additional Information (some from lead, some new)
    Requirements: '',
    NextStepAction: '',
    Notes: lead.Notes || '',
    
    // Flags
    IsActive: true,
    IsHighValue: false
  });

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validate = () => {
    const errs = {};
    
    // Required fields
    if (!formData.OpportunityName.trim()) {
      errs.OpportunityName = t('opportunities.validation.opportunityNameRequired', 'Opportunity name is required');
    }
    
    if (!formData.CustomerName.trim()) {
      errs.CustomerName = t('opportunities.validation.customerNameRequired', 'Customer name is required');
    }
    
    if (!formData.Value || formData.Value <= 0) {
      errs.Value = t('opportunities.validation.valueRequired', 'Value must be greater than 0');
    }
    
    if (!formData.Probability || formData.Probability < 0 || formData.Probability > 100) {
      errs.Probability = t('opportunities.validation.probabilityRange', 'Probability must be between 0 and 100');
    }
    
    if (!formData.ExpectedCloseDate) {
      errs.ExpectedCloseDate = t('opportunities.validation.expectedCloseDateRequired', 'Expected close date is required');
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const submitData = { ...formData };
      
      // Convert numeric fields
      if (submitData.Value) {
        submitData.Value = parseFloat(submitData.Value);
      }
      if (submitData.Probability) {
        submitData.Probability = parseInt(submitData.Probability);
      }
      
      // Convert empty strings to null
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      await onSubmit(lead.LeadID, submitData);
    } catch (err) {
      console.error('Conversion error:', err);
      const status = err.status;
      const serverDetail = err.serverError;
      let displayMessage = err.message || 'Failed to convert lead to opportunity';
      if (serverDetail && serverDetail !== displayMessage) {
        displayMessage = `${displayMessage}: ${serverDetail}`;
      }
      if (status) {
        displayMessage = `(${status}) ${displayMessage}`;
      }
      setErrors({ submit: displayMessage });
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    { value: 'Low', label: t('opportunities.priorityLow', 'Low') },
    { value: 'Medium', label: t('opportunities.priorityMedium', 'Medium') },
    { value: 'High', label: t('opportunities.priorityHigh', 'High') },
    { value: 'Critical', label: t('opportunities.priorityCritical', 'Critical') },
  ];

  const categoryOptions = [
    { value: 'New Business', label: t('opportunities.categoryNewBusiness', 'New Business') },
    { value: 'Existing Business', label: t('opportunities.categoryExistingBusiness', 'Existing Business') },
    { value: 'Renewal', label: t('opportunities.categoryRenewal', 'Renewal') },
    { value: 'Upsell', label: t('opportunities.categoryUpsell', 'Upsell') },
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'GBP', label: 'GBP (£)' },
    { value: 'THB', label: 'THB (฿)' },
    { value: 'JPY', label: 'JPY (¥)' },
  ];


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('pipeline.convertToOpportunity', 'Convert to Opportunity')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('pipeline.convertSubtitle', 'Fill in additional details to convert this lead to an opportunity')}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Lead Information (Read-only) */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <Target className="h-5 w-5 mr-2" />
              {t('pipeline.leadInformation', 'Lead Information')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium">
                  {t('leads.contactName', 'Contact Name')}
                </label>
                <p className="text-gray-900 dark:text-white">{lead.ContactName || '-'}</p>
              </div>
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium">
                  {t('leads.companyName', 'Company Name')}
                </label>
                <p className="text-gray-900 dark:text-white">{lead.CompanyName || '-'}</p>
              </div>
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium">
                  {t('leads.score', 'Score')}
                </label>
                <p className="text-gray-900 dark:text-white">{lead.Score || '-'}/100</p>
              </div>
              <div>
                <label className="block text-gray-700 dark:text-gray-300 font-medium">
                  {t('leads.estimatedBudget', 'Estimated Budget')}
                </label>
                <p className="text-gray-900 dark:text-white">
                  {lead.EstimatedBudget 
                    ? `${lead.EstimatedBudget} ${lead.Currency || 'USD'}`
                    : '-'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              {t('opportunities.basicInfo', 'Basic Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.opportunityName', 'Opportunity Name')} *
                </label>
                <input
                  type="text"
                  value={formData.OpportunityName}
                  onChange={(e) => handleFieldChange('OpportunityName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${errors.OpportunityName ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                  placeholder={t('opportunities.opportunityNamePlaceholder', 'Enter opportunity name')}
                />
                {errors.OpportunityName && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.OpportunityName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.priority', 'Priority')}
                </label>
                <select
                  value={formData.Priority}
                  onChange={(e) => handleFieldChange('Priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.category', 'Category')}
                </label>
                <select
                  value={formData.Category}
                  onChange={(e) => handleFieldChange('Category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {categoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.description', 'Description')}
                </label>
                <textarea
                  value={formData.Description}
                  onChange={(e) => handleFieldChange('Description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.descriptionPlaceholder', 'Enter opportunity description')}
                />
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Building className="h-5 w-5 mr-2" />
              {t('opportunities.customerInfo', 'Customer Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.customerName', 'Customer Name')} *
                </label>
                <input
                  type="text"
                  value={formData.CustomerName}
                  onChange={(e) => handleFieldChange('CustomerName', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${errors.CustomerName ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {errors.CustomerName && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.CustomerName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.contactName', 'Contact Name')}
                </label>
                <input
                  type="text"
                  value={formData.ContactName}
                  onChange={(e) => handleFieldChange('ContactName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.contactEmail', 'Contact Email')}
                </label>
                <input
                  type="email"
                  value={formData.ContactEmail}
                  onChange={(e) => handleFieldChange('ContactEmail', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.contactPhone', 'Contact Phone')}
                </label>
                <input
                  type="tel"
                  value={formData.ContactPhone}
                  onChange={(e) => handleFieldChange('ContactPhone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <DollarSign className="h-5 w-5 mr-2" />
              {t('opportunities.financialInfo', 'Financial Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.value', 'Value')} *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.Value}
                  onChange={(e) => handleFieldChange('Value', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${errors.Value ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {errors.Value && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.Value}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.currency', 'Currency')}
                </label>
                <select
                  value={formData.Currency}
                  onChange={(e) => handleFieldChange('Currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {currencyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.probability', 'Probability')} (%) *
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.Probability}
                  onChange={(e) => handleFieldChange('Probability', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${errors.Probability ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {errors.Probability && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.Probability}</p>
                )}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              {t('opportunities.timeline', 'Timeline')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.expectedCloseDate', 'Expected Close Date')} *
                </label>
                <input
                  type="date"
                  value={formData.ExpectedCloseDate}
                  onChange={(e) => handleFieldChange('ExpectedCloseDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${errors.ExpectedCloseDate ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                />
                {errors.ExpectedCloseDate && (
                  <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.ExpectedCloseDate}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.nextFollowUpDate', 'Next Follow-up Date')}
                </label>
                <input
                  type="date"
                  value={formData.NextFollowUpDate}
                  onChange={(e) => handleFieldChange('NextFollowUpDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Star className="h-5 w-5 mr-2" />
              {t('opportunities.additionalInfo', 'Additional Information')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.requirements', 'Requirements')}
                </label>
                <textarea
                  value={formData.Requirements}
                  onChange={(e) => handleFieldChange('Requirements', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.requirementsPlaceholder', 'Enter customer requirements')}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.nextStepAction', 'Next Step Action')}
                </label>
                <textarea
                  value={formData.NextStepAction}
                  onChange={(e) => handleFieldChange('NextStepAction', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.nextStepActionPlaceholder', 'What is the next action required?')}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('opportunities.notes', 'Notes')}
                </label>
                <textarea
                  value={formData.Notes}
                  onChange={(e) => handleFieldChange('Notes', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                  placeholder={t('opportunities.notesPlaceholder', 'Additional notes')}
                />
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4" role="alert">
              <p className="font-medium text-red-800 dark:text-red-300">
                {t('pipeline.conversionFailed', 'Conversion failed')}
              </p>
              <p className="text-red-600 dark:text-red-400 text-sm mt-1 break-words">{errors.submit}</p>
              <p className="text-red-500 dark:text-red-500/90 text-xs mt-2">
                {t('pipeline.conversionErrorHint', 'Check required fields (name, customer, value, probability, close date) or contact your administrator if the problem persists.')}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>
                {loading 
                  ? t('common.converting', 'Converting...') 
                  : t('pipeline.convertToOpportunity', 'Convert to Opportunity')
                }
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadToOpportunityModal; 